import { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, ShieldCheck, Lightbulb, Wrench, Send, CheckCircle2, HardHat, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AddressSearchBar } from "@/components/AddressSearchBar";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { downloadReportPdf } from "@/lib/reportPdf";
import { ProfessionalAssistance } from "@/components/ProAssistance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [English id sent to AI, i18n label key]
const PROJECTS = [
  ["Remodel", "cp.remodel"], ["Addition", "cp.addition"], ["New Construction", "cp.newconstruction"],
  ["Legalize Existing Work", "cp.legalize"], ["Change of Use", "cp.changeuse"], ["Duplex / Multifamily", "cp.duplex"],
  ["Windows & Doors", "cp.windows"], ["Roof", "cp.roof"], ["Pool", "cp.pool"], ["Electrical", "cp.electrical"],
  ["Plumbing", "cp.plumbing"], ["Mechanical / A/C", "cp.mechanical"], ["Other", "cp.other"],
];
const ISSUES = [
  ["Open Permit", "ci.openpermit"], ["Expired Permit", "ci.expiredpermit"], ["Violation", "ci.violation"],
  ["Work Without Permit", "ci.nowork"], ["Failed Inspection", "ci.failedinsp"], ["Pending Inspection", "ci.pendinginsp"],
  ["Plan Review Comments", "ci.planreview"], ["Legalize Existing Work", "ci.legalize"], ["Permit Closure", "ci.closure"],
  ["Certificate of Completion / Occupancy", "ci.certificate"], ["Other", "ci.other"],
];

const AnswerBlock = ({ text }) => {
  const parts = text.split(/(VERIFIED PROPERTY DATA|VERIFIED OFFICIAL DATA|PROPERTYCHECK GUIDANCE|DATOS VERIFICADOS DE LA PROPIEDAD|ORIENTACIÓN DE WISE PROPERTY SEARCH)/g);
  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-foreground" data-testid="advisor-answer">
      {parts.map((p, i) => {
        if (p.startsWith("VERIFIED") || p === "DATOS VERIFICADOS DE LA PROPIEDAD")
          return <p key={i} className="mt-2 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-3.5 w-3.5" />{p}</p>;
        if (p === "PROPERTYCHECK GUIDANCE" || p === "ORIENTACIÓN DE WISE PROPERTY SEARCH")
          return <p key={i} className="mt-2 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-accent"><Lightbulb className="h-3.5 w-3.5" />{p}</p>;
        return <p key={i} className="whitespace-pre-wrap">{p.trim()}</p>;
      })}
    </div>
  );
};

const PropChip = ({ report }) => (
  <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary" data-testid="active-property-chip">
    <ShieldCheck className="h-3.5 w-3.5" />
    {(report.site_address || report.address)} · {report.jurisdiction}
  </div>
);

const AssistanceForm = ({ report, analysis, extra }) => {
  const { t } = useLang();
  const [mode, setMode] = useState(null); // null | "general" | "contractor"
  const [form, setForm] = useState({ name: "", email: "", phone: "", description: "" });
  const submit = async () => {
    if (!form.name || !form.email) return toast.error(t("assist.nameEmail"));
    try {
      await axios.post(`${API}/permit-assistance`, {
        ...form,
        request_type: mode === "contractor" ? "contractor" : "general",
        folio: report?.folio,
        address: report?.site_address || report?.address,
        description: `${form.description}\n\n[Context] ${extra || ""}\n[Analysis] ${(analysis || "").slice(0, 1500)}`,
      });
      toast.success(t("ask.submitted"));
      setMode(null);
      setForm({ name: "", email: "", phone: "", description: "" });
    } catch {
      toast.error(t("assist.submitErr"));
    }
  };
  return (
    <div className="mt-5 rounded-lg border border-border bg-secondary/40 p-4">
      <p className="mb-2 text-sm font-semibold text-foreground">{mode === "contractor" ? t("assist.contractorTitle") : t("assist.title")}</p>
      {!mode ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="request-help-btn" onClick={() => setMode("general")}>{t("assist.btn")}</Button>
          <Button data-testid="request-contractor-btn" className="gap-2" onClick={() => setMode("contractor")}>
            <HardHat className="h-4 w-4" />{t("assist.contractorBtn")}
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="help-form">
          {mode === "contractor" && <p className="text-xs text-muted-foreground">{t("assist.contractorDesc")}</p>}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder={t("ask.name")} data-testid="help-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder={t("ask.email")} type="email" data-testid="help-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <Input placeholder={t("assist.phone")} data-testid="help-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Textarea placeholder={t("assist.sit")} data-testid="help-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <Button data-testid="help-submit" onClick={submit}>{t("ask.submit")}</Button>
            <Button variant="ghost" onClick={() => setMode(null)}>×</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ProjectAdvisor = ({ report, onSearch }) => {
  const { t, lang } = useLang();
  const [project, setProject] = useState(null);
  const [desc, setDesc] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const { track, user, openAuth } = useAuth();

  const analyze = async () => {
    if (loading) return;
    setLoading(true);
    const q = `I am planning a project on this property. Project type: ${project || "described below"}. Description: ${desc || project}. Give a preliminary roadmap: likely permits, permit disciplines, zoning considerations, likely drawings/engineering, professionals required, likely inspections, existing property issues (permits/violations) that may affect it, and recommended next steps.`;
    try {
      const { data } = await axios.post(`${API}/ask`, { question: q, project_type: project, context: report, lang });
      setAnswer(data.answer || data.message || "…");
      track("project_advisor", { project_type: project, folio: report?.folio });
    } catch { setAnswer("The advisor is temporarily unavailable. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="mb-1 flex items-center gap-2"><Sparkles className="h-6 w-6 text-accent" /><h1 className="font-serif text-3xl font-semibold text-foreground">{t("pa.title")}</h1></div>
      <p className="mb-6 text-base text-muted-foreground">{t("pa.subtitle")}</p>

      {!report ? (
        <div className="rounded-xl border border-border bg-card p-6">
          <p className="mb-3 text-sm font-medium text-foreground">{t("pa.startAddr")}</p>
          <AddressSearchBar onSearch={onSearch} loading={false} />
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="project-advisor-panel">
          <PropChip report={report} />
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t("pa.what")}</p>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {PROJECTS.map(([id, key]) => (
              <button key={id} data-testid="advisor-project-chip" onClick={() => setProject(project === id ? null : id)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${project === id ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/50 text-foreground/80 hover:border-primary/50"}`}>{t(key)}</button>
            ))}
          </div>
          <Textarea data-testid="advisor-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("pa.descph")} className="mb-3" />
          <Button onClick={analyze} disabled={loading} data-testid="advisor-analyze-btn" className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("pa.analyze")}
          </Button>

          {answer && (
            <div className="mt-5 rounded-lg border border-border bg-secondary/40 p-4">
              <AnswerBlock text={answer} />
              <Button variant="outline" size="sm" data-testid="download-report-pdf-btn" className="mt-3 gap-1.5"
                onClick={() => { if (!user) { openAuth(); return; } downloadReportPdf(report, answer, "Wise Property Search Project Report"); }}>
                <Download className="h-3.5 w-3.5" />{t("export.pdf")}
              </Button>
            </div>
          )}

          <div className="mt-5"><ProfessionalAssistance report={report} /></div>
          <p className="mt-4 border-t border-border pt-3 text-[11px] text-muted-foreground">{t("ask.disclaimer")}</p>
        </div>
      )}
    </div>
  );
};

export const PermitHelp = ({ report, onSearch }) => {
  const { t, lang } = useLang();
  const { track } = useAuth();
  const [issue, setIssue] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const analyze = async (selected) => {
    setIssue(selected);
    setLoading(true);
    const q = `The user needs help with this issue: "${selected}". Explain what the current status means, what issue was identified, which authority is responsible, what may need to happen next, whether plans are likely required, whether a contractor/professional may be required, whether inspections may still be required, and what information is still missing.`;
    try {
      const { data } = await axios.post(`${API}/ask`, { question: q, project_type: selected, context: report, lang });
      setAnswer(data.answer || data.message || "…");
      track("permit_help", { project_type: selected, folio: report?.folio });
    } catch { setAnswer("The advisor is temporarily unavailable. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="mb-1 flex items-center gap-2"><Wrench className="h-6 w-6 text-accent" /><h1 className="font-serif text-3xl font-semibold text-foreground">{t("ph.title")}</h1></div>
      <p className="mb-6 text-base text-muted-foreground">{t("ph.subtitle")}</p>

      {!report ? (
        <div className="rounded-xl border border-border bg-card p-6">
          <p className="mb-3 text-sm font-medium text-foreground">{t("ph.startAddr")}</p>
          <AddressSearchBar onSearch={onSearch} loading={false} />
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="permit-help-panel">
          <PropChip report={report} />
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t("ph.what")}</p>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {ISSUES.map(([id, key]) => (
              <button key={id} data-testid="help-issue-chip" onClick={() => analyze(id)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${issue === id ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/50 text-foreground/80 hover:border-primary/50"}`}>{t(key)}</button>
            ))}
          </div>
          {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> …</div>}
          {answer && !loading && <div className="rounded-lg border border-border bg-secondary/40 p-4"><AnswerBlock text={answer} /></div>}
          <div className="mt-5"><ProfessionalAssistance report={report} defaultService="Permit advisor" /></div>
          <p className="mt-4 border-t border-border pt-3 text-[11px] text-muted-foreground">{t("ask.disclaimer")}</p>
        </div>
      )}
    </div>
  );
};
